import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { FormGroup } from '../../../ui/gzForm/formGroup/form-group';
import { AccountRoleAndPrivilegesWebComponent } from '../../common/accountRoleAndPrivileges';
import {
  WEB_COMPONENT_ACCOUNT_DETAILS,
  WEB_COMPONENT_ACCOUNT_LOGIN_SECURITY,
  WEB_COMPONENT_ACCOUNT_ROLE_AND_PRIVILEGES,
  WEB_COMPONENT_TARGETS,
} from '../../../webcomponents.constants';
import { translateFn } from '../../../utils/translator.helper';
import { getAccountsStrings } from '../../common/accountStrings/accountStrings';
import { AccountLoginSecurityWebComponent } from '../../common/accountLoginSecurity';
import { AccountDetailsWebComponent } from '../../common/accountDetails';
import { TargetsWebComponent } from '../../../targets';
import { AccountActionContext } from '../../common/types/AccountActionContext';
import { AccountDetails } from '../../common/accountDetails/types';
import { TemplateState } from '../../../types/common/types';
import { CompanyData } from '../../common/types/CompanyData';
import { EditedAccount } from '../types';
import { AccountLoginSecurityData } from '../../common/accountLoginSecurity/types';
import { AccountRoleAndPrivilegesData } from '../../common/accountRoleAndPrivileges/type';
import { AccountSource, AccountSourceId } from '../../common/types/AccountSources';
import { SelectedTargets, TargetsViewData } from '../../../targets/type';
import { toggleVisibility } from '../../../utils/htmlElement.utils';
import { CreateAccountEvents } from '../../addAccount/types';
import { renderSelectTargetsComponent } from '../../helpers/renderSelectTargetsComponent';

export class EditAccountWebView extends BaseWebComponentView {
  public formGroup: FormGroup;
  public dom: {
    $wrapper: HTMLElement;
    $formWrapper: HTMLElement;

    $details: AccountDetailsWebComponent;
    $loginSecurity: AccountLoginSecurityWebComponent;
    $roleAndPrivileges: AccountRoleAndPrivilegesWebComponent;
    $targetsSelection: TargetsWebComponent;

    $updateButton: GzButtonWebComponent;
    $cancelButton: GzButtonWebComponent;
  };

  public accountSource: AccountSourceId = AccountSource.MANUAL;
  public currentCompany: CompanyData;
  public preselectedTargets: SelectedTargets | undefined;

  getDomElements(): void {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),
      $formWrapper: this.shadow.querySelector('.form-wrapper'),

      $details: this.shadow.querySelector(WEB_COMPONENT_ACCOUNT_DETAILS),
      $loginSecurity: this.shadow.querySelector(WEB_COMPONENT_ACCOUNT_LOGIN_SECURITY),
      $roleAndPrivileges: this.shadow.querySelector(WEB_COMPONENT_ACCOUNT_ROLE_AND_PRIVILEGES),
      $targetsSelection: this.shadow.querySelector(WEB_COMPONENT_TARGETS),

      $updateButton: this.shadow.querySelector('#save-button'),
      $cancelButton: this.shadow.querySelector('#cancel-button'),
    };

    this.setLoadingState();

    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations(): any {
    this.translations = getAccountsStrings();
    return this.translations;
  }

  setTranslatedStrings(): void {
    this.dom.$updateButton.innerText = this.commonStrings.save;
    this.dom.$cancelButton.innerText = translateFn('Cancel');
  }

  disconnected() {}

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }

  async buildSuccessView(viewData: EditedAccount): Promise<void> {
    this.dom.$details.injectDependencies(this.dependencies);
    this.dom.$details.setActionContext(AccountActionContext.Edit);
    this.dom.$loginSecurity.injectDependencies(this.dependencies);
    this.dom.$loginSecurity.setActionContext(AccountActionContext.Edit);
    this.dom.$roleAndPrivileges.injectDependencies(this.dependencies);
    this.dom.$roleAndPrivileges.setActionContext(AccountActionContext.Edit);

    let targetsShouldBeVisible = false;
    if (viewData) {
      this.currentCompany = viewData.companyInfo;
      targetsShouldBeVisible = true;

      const accountDetails = {
        _id: viewData._id,
        email: viewData.email,
        profile: viewData.profile,
        userName: viewData.userName,
        phoneNumber: viewData.phoneNumber,
        source: viewData.source,
        companyInfo: viewData.companyInfo,
      } as AccountDetails;

      const accountLoginSecurity = {
        _id: viewData._id,
        twoFactorAuthentication: viewData.twoFactorAuthentication,
        passwordLifetime: viewData.passwordLifetime,
        accountLockdown: viewData.accountLockdown,
        concurrentSessions: viewData.concurrentSessions,
        source: viewData.source,
        companyInfo: viewData.companyInfo,
      } as AccountLoginSecurityData;

      const accountRoleAndPrivileges = {
        _id: viewData._id,
        role: viewData.role,
        companyInfo: viewData.companyInfo,
      } as AccountRoleAndPrivilegesData;

      this.preselectedTargets = viewData.serviceTargets;

      this.dom.$details.setViewData(accountDetails);
      this.dom.$loginSecurity.setViewData(accountLoginSecurity);
      this.dom.$roleAndPrivileges.setViewData(accountRoleAndPrivileges);
    }

    await this.dom.$details.start();
    await this.dom.$loginSecurity.start();
    await this.dom.$roleAndPrivileges.start();

    // start targets component if it should be visible
    if (targetsShouldBeVisible && this.dom.$roleAndPrivileges.mustSelectTargets()) {
      const targetsViewData = {
        companyId: this.currentCompany._id,
        availableServices: this.dependencies.getAvailableServices(),
      } as TargetsViewData;

      if (this.preselectedTargets) {
        targetsViewData.preSelectedTargets = this.preselectedTargets;
      }
      await renderSelectTargetsComponent(this.dom.$targetsSelection, targetsViewData, this.dependencies);
      toggleVisibility(this.dom.$targetsSelection, true);
    }

    this.dom.$wrapper.dispatchEvent(
      new CustomEvent(CreateAccountEvents.InitCreateAccountForm, {
        bubbles: true,
        composed: true,
      })
    );
    this.removeLoadingState();
  }
}
